import Button from "@cloudscape-design/components/button";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { useMemo, useState } from "react";
import { api, errorMessage } from "../../api/client";
import type { MessageResponse } from "../../api/types";
import { useValidatedForm } from "../../lib/useValidatedForm";
import { confirmationRules, passwordRules, PASSWORD_CONSTRAINT, type ValidationConfig } from "../../lib/validation";
import { useQueryParam } from "../../router/useRoute";
import { PublicShell } from "./PublicShell";
import { ResultPage } from "./ResultPage";

type Field = "password" | "passwordConfirm";
const FIELD_ORDER = ["password", "passwordConfirm"] as const satisfies readonly Field[];

export function ResetPasswordPage() {
  const resetToken = useQueryParam("token");
  const [password, setPassword] = useState("");
  const config = useMemo<ValidationConfig>(
    () => ({ password: passwordRules, passwordConfirm: confirmationRules(() => password) }),
    [password],
  );
  const form = useValidatedForm<Field>(config, { password: "", passwordConfirm: "" }, FIELD_ORDER);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFormError("");
    if (!form.submit()) return;
    if (!resetToken) {
      setFormError("Liên kết đặt lại mật khẩu không hợp lệ hoặc đã hết hạn. Yêu cầu liên kết mới từ trang quên mật khẩu.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await api<MessageResponse>("/auth/reset-password", {
        method: "POST",
        body: { token: resetToken, password: form.values.password },
      });
      setDone(result.message);
    } catch (error) {
      setFormError(errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return <ResultPage title="Đã đổi mật khẩu" detail={done} actionLabel="Đăng nhập" actionHref="/login" />;
  }

  return (
    <PublicShell
      title="Đặt mật khẩu mới"
      description="Sau khi hoàn tất, mọi phiên đăng nhập cũ sẽ bị thu hồi."
    >
      <form onSubmit={handleSubmit}>
        <Form
          errorText={formError || undefined}
          errorIconAriaLabel="Lỗi"
          actions={
            <Button variant="primary" loading={submitting} loadingText="Đang cập nhật">
              Cập nhật mật khẩu
            </Button>
          }
        >
          <SpaceBetween size="l">
            <FormField label="Mật khẩu mới" constraintText={PASSWORD_CONSTRAINT} errorText={form.errors.password}>
              <Input
                ref={form.refFor("password")}
                type="password"
                autoComplete="new-password"
                value={form.values.password}
                onChange={({ detail }) => {
                  setPassword(detail.value);
                  form.setValue("password", detail.value);
                }}
                onBlur={() => form.handleBlur("password")}
              />
            </FormField>
            <FormField label="Nhập lại mật khẩu" errorText={form.errors.passwordConfirm}>
              <Input
                ref={form.refFor("passwordConfirm")}
                type="password"
                autoComplete="new-password"
                value={form.values.passwordConfirm}
                onChange={({ detail }) => form.setValue("passwordConfirm", detail.value)}
                onBlur={() => form.handleBlur("passwordConfirm")}
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </form>
    </PublicShell>
  );
}
